import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { proposalAPI, clientAPI, serviceAPI, calculatorAPI } from '../lib/api';
import { formatINR, formatPercent, getMarginBadgeClass, apiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import DatePicker from '../components/DatePicker';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ClientPicker from '../components/ClientPicker';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Plus,
  Trash2,
  Calculator,
  FileText,
  Loader2,
  AlertTriangle
} from 'lucide-react';

const PAYMENT_TERMS = ['GST', 'Non-GST', 'Cash'];
const STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'in_progress', 'completed'];

export default function ProposalForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    project_name: '',
    due_date: '',
    deliverables: [],
    labor_cost: 0,
    overhead_cost: 0,
    direct_cost: 0,
    total_cost: 0,
    proposed_price: 0,
    discount_percentage: 0,
    discount_amount: 0,
    final_price: 0,
    margin_percentage: 0,
    payment_terms: 'Non-GST',
    payment_schedule: '',
    notes: '',
    status: 'draft'
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientsRes, servicesRes] = await Promise.all([
        clientAPI.getAll(),
        serviceAPI.getAll()
      ]);
      setClients(clientsRes.data);
      setServices(servicesRes.data);

      if (isEditing) {
        const proposalRes = await proposalAPI.getOne(id);
        setFormData(proposalRes.data);
      }
    } catch (error) {
      toast.error(apiError(error, 'Failed to fetch data'));
      if (isEditing) navigate('/proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData({
      ...formData,
      client_id: clientId,
      client_name: client?.name || '',
      payment_terms: client?.payment_terms || 'Non-GST'
    });
  };

  const addDeliverable = () => {
    setFormData({
      ...formData,
      deliverables: [
        ...formData.deliverables,
        { name: '', quantity: 1, unit_price: 0, time_hours: 0 }
      ]
    });
  };

  const addServiceDeliverable = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setFormData({
        ...formData,
        deliverables: [
          ...formData.deliverables,
          { 
            name: service.name, 
            quantity: 1, 
            unit_price: service.selling_price, 
            time_hours: service.avg_time_hours,
            service_id: service.id
          }
        ]
      });
    }
  };

  const updateDeliverable = (index, field, value) => {
    const newDeliverables = [...formData.deliverables];
    newDeliverables[index] = { ...newDeliverables[index], [field]: value };
    setFormData({ ...formData, deliverables: newDeliverables });
  };

  const removeDeliverable = (index) => {
    setFormData({
      ...formData,
      deliverables: formData.deliverables.filter((_, i) => i !== index)
    });
  };

  const calculateCosts = async () => {
    setCalculating(true);
    try {
      // Calculate from deliverables
      const subtotal = formData.deliverables.reduce((sum, d) => sum + (d.quantity * d.unit_price), 0);
      const totalHours = formData.deliverables.reduce((sum, d) => sum + (d.quantity * d.time_hours), 0);
      
      // Simple cost calculation (you can use the backend calculator for more complex logic)
      const laborCost = subtotal * 0.6; // Assuming 60% of price is labor
      const overheadCost = laborCost * 0.15; // 15% overhead
      const directCost = parseFloat(formData.direct_cost) || 0;
      const totalCost = laborCost + overheadCost + directCost;
      
      const proposedPrice = subtotal;
      const discountAmount = proposedPrice * (parseFloat(formData.discount_percentage) || 0) / 100;
      const finalPrice = proposedPrice - discountAmount;
      const marginPercentage = finalPrice > 0 ? ((finalPrice - totalCost) / finalPrice) * 100 : 0;

      setFormData({
        ...formData,
        labor_cost: Math.round(laborCost),
        overhead_cost: Math.round(overheadCost),
        total_cost: Math.round(totalCost),
        proposed_price: Math.round(proposedPrice),
        discount_amount: Math.round(discountAmount),
        final_price: Math.round(finalPrice),
        margin_percentage: Math.round(marginPercentage * 100) / 100
      });

      toast.success('Costs calculated');
    } catch (error) {
      toast.error('Failed to calculate costs');
    } finally {
      setCalculating(false);
    }
  };

  const handleDiscountChange = (discountPercent) => {
    const discount = parseFloat(discountPercent) || 0;
    const discountAmount = formData.proposed_price * discount / 100;
    const finalPrice = formData.proposed_price - discountAmount;
    const marginPercentage = finalPrice > 0 ? ((finalPrice - formData.total_cost) / finalPrice) * 100 : 0;

    setFormData({
      ...formData,
      discount_percentage: discount,
      discount_amount: Math.round(discountAmount),
      final_price: Math.round(finalPrice),
      margin_percentage: Math.round(marginPercentage * 100) / 100
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) return toast.error('Pick a client');
    setSaving(true);

    try {
      if (isEditing) {
        await proposalAPI.update(id, formData);
        toast.success('Proposal updated successfully');
      } else {
        await proposalAPI.create(formData);
        toast.success('Proposal created successfully');
      }
      navigate('/proposals');
    } catch (error) {
      toast.error(apiError(error, 'Failed to save proposal'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/proposals')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
            {isEditing ? 'Edit Proposal' : 'Create Proposal'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditing ? 'Update proposal details' : 'Build a new proposal with live cost calculation'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client & Project Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Select Client *</Label>
                    <ClientPicker
                      clients={clients}
                      value={formData.client_id}
                      onChange={handleClientChange}
                      placeholder={formData.client_name || 'Choose a client'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="projectName">Project Name *</Label>
                    <Input
                      id="projectName"
                      data-testid="proposal-project-input"
                      value={formData.project_name}
                      onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dueDate">Due Date *</Label>
                    <DatePicker
                      id="dueDate"
                      data-testid="proposal-duedate-input"
                      value={formData.due_date}
                      onChange={(v) => setFormData({ ...formData, due_date: v })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(v) => setFormData({ ...formData, status: v })}
                    >
                      <SelectTrigger data-testid="proposal-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(status => (
                          <SelectItem key={status} value={status} className="capitalize">{status}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Deliverables */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Scope of Work</CardTitle>
                <div className="flex gap-2">
                  <Select value="" onValueChange={addServiceDeliverable}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Add from services" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(service => (
                        <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" onClick={addDeliverable}>
                    <Plus className="w-4 h-4 mr-1" />
                    Custom
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {formData.deliverables.length > 0 ? (
                    <div className="overflow-x-auto">
                      <div className="min-w-[640px]">
                      <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase">
                        <div className="col-span-5">Deliverable</div>
                        <div className="col-span-2 text-center">Qty</div>
                        <div className="col-span-2 text-right">Unit Price</div>
                        <div className="col-span-2 text-right">Amount</div>
                        <div className="col-span-1"></div>
                      </div>
                      {formData.deliverables.map((item, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 items-center">
                          <div className="col-span-5">
                            <Input
                              value={item.name}
                              onChange={(e) => updateDeliverable(index, 'name', e.target.value)}
                              placeholder="Deliverable name"
                              className="truncate"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateDeliverable(index, 'quantity', parseInt(e.target.value) || 1)}
                              className="text-center"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => updateDeliverable(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="text-right font-mono"
                            />
                          </div>
                          <div className="col-span-2 text-right font-mono">
                            {formatINR(item.quantity * item.unit_price)}
                          </div>
                          <div className="col-span-1 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDeliverable(index)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No deliverables added yet</p>
                      <p className="text-sm">Add services or custom items above</p>
                    </div>
                  )}
                </div>

                {formData.deliverables.length > 0 ? (
                  <div className="mt-6 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={calculateCosts}
                      disabled={calculating}
                      className="w-full"
                    >
                      {calculating ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Calculator className="w-4 h-4 mr-2" />
                      )}
                      Calculate Costs
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mt-4">Add deliverables above to calculate costs</p>
                )}
              </CardContent>
            </Card>

            {/* Payment & Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Payment & Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="paymentTerms">Payment Terms</Label>
                    <Select 
                      value={formData.payment_terms} 
                      onValueChange={(v) => setFormData({ ...formData, payment_terms: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_TERMS.map(term => (
                          <SelectItem key={term} value={term}>{term}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="paymentSchedule">Payment Schedule</Label>
                    <Input
                      id="paymentSchedule"
                      value={formData.payment_schedule}
                      onChange={(e) => setFormData({ ...formData, payment_schedule: e.target.value })}
                      placeholder="e.g., 50% advance, 50% on delivery"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes for the proposal..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Cost Breakdown */}
          <div className="space-y-6">
            <Card className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto">
              <CardHeader>
                <CardTitle className="text-lg">Cost Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Cost items */}
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Labor Cost</span>
                      <span className="font-mono">{formatINR(formData.labor_cost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Overhead</span>
                      <span className="font-mono">{formatINR(formData.overhead_cost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Direct Costs</span>
                      <Input
                        type="number"
                        value={formData.direct_cost}
                        onChange={(e) => setFormData({ ...formData, direct_cost: parseFloat(e.target.value) || 0 })}
                        className="w-32 text-right font-mono h-8"
                      />
                    </div>
                    <div className="flex justify-between font-medium pt-3 border-t">
                      <span>Total Cost (COGS)</span>
                      <span className="font-mono">{formatINR(formData.total_cost)}</span>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div className="pt-4 border-t space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Proposed Price</span>
                      <span className="font-mono">{formatINR(formData.proposed_price)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Discount %</span>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.discount_percentage}
                        onChange={(e) => handleDiscountChange(e.target.value)}
                        className="w-20 text-right font-mono h-8"
                      />
                    </div>
                    {formData.discount_amount > 0 && (
                      <div className="flex justify-between text-sm text-red-600">
                        <span>Discount Amount</span>
                        <span className="font-mono">-{formatINR(formData.discount_amount)}</span>
                      </div>
                    )}
                  </div>

                  {/* Final Price */}
                  <div className="bg-black text-white p-4 -mx-6 -mb-6 rounded-b-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Final Price</span>
                      <span className="text-2xl font-bold font-mono">{formatINR(formData.final_price)}</span>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-gray-300">Margin</span>
                      <span className={`badge ${getMarginBadgeClass(formData.margin_percentage)}`}>
                        {formatPercent(formData.margin_percentage)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Margin Warning */}
                {formData.margin_percentage > 0 && formData.margin_percentage < 20 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Low Margin Warning</p>
                      <p className="text-xs text-red-600">Margin is below the minimum threshold of 20%</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit Buttons */}
            <div className="flex flex-col gap-3">
              <Button 
                type="submit" 
                className="w-full bg-black hover:bg-black/90"
                disabled={saving}
                data-testid="save-proposal-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                {isEditing ? 'Update Proposal' : 'Create Proposal'}
              </Button>
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate('/proposals')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
